<template>
    <v-app>
        <header-card-public current_page="login"></header-card-public>
        <v-content class="bg-container">
            <v-layout fill-height row wrap>
                <!-- Left/top Side: Ads -->
                <v-flex v-if="!isMobile" xs12 sm6 class="ads-section">
                    <div class="ads-bg">
                        <public-notifications></public-notifications>
                    </div>
                </v-flex>

                <!-- Right/bottom Side: Welcome Card -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout fill-height align-center justify-center>
                        <div class="login-container">
                            <v-card class="mx-auto login-card" max-width="500">
                                <v-form @submit.prevent="executeRecaptcha" ref="form">
                                    <v-card-text>
                                        <h3 v-if="isRecaptchaLoaded" class="text-xs-center">LOGIN</h3>
                                        <h3 v-if="!isRecaptchaLoaded" class="text-xs-center">Loading components...</h3>
                                        <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                                            {{errorMessage}}
                                        </v-alert>
                                        <v-alert v-if="slowInternet" :value="true" color="error" icon="error" outline>
                                            Internet connection is slow. Some features may not work properly. 
                                        </v-alert>
                                        <v-text-field
                                            class="body-1 orange--text"
                                            style="margin-top:25px;"
                                            prepend-inner-icon="email"
                                            v-model="email"
                                            label="Email"
                                            :rules="[rules.required,rules.email,rules.min,rules.max]"
                                            type="text"
                                            :disabled="disabled||isNoInternet"
                                        ></v-text-field>
                                        <v-text-field
                                            class="body-1"
                                            prepend-inner-icon="lock"
                                            v-model="password"
                                            :append-icon="show1 ? 'visibility_off' : 'visibility'"
                                            :rules="[rules.required,rules.counter]"
                                            :type="show1 ? 'text' : 'password'"
                                            label="Password"
                                            @click:append="show1 = !show1"
                                            :disabled="disabled||isNoInternet"
                                        ></v-text-field>

                                        <v-btn v-if="!slowInternet" :color="welcomeButtonColor" type="submit" :loading="loading" block dark :disabled="isNoInternet">
                                            <v-icon>login</v-icon>&nbsp;&nbsp;Login
                                        </v-btn>
                                        <v-btn v-if="slowInternet" :color="welcomeButtonColor" block dark @click="reloadPage">Refresh Page</v-btn>
                                    </v-card-text>
                                </v-form>
                                <v-divider></v-divider>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn small flat to="/PasswordRecovery" :disabled="disabled||isNoInternet||!isRecaptchaLoaded" class="header-text caption">I forgot my password</v-btn>
                                </v-card-actions>
                            </v-card>
                            <v-card class="mt-2 mx-auto transparent elevation-0 text-xs-center" max-width="420">
                                <terms-privacy></terms-privacy>
                            </v-card>
                        </div>
                        <div class="footer-section caption">
                            <div class="white--text text-xs-center">&copy; Copyright 2024 SweldoNow OPC. All Rights Reserved.</div>
                            <other-legal-docs v-if="!isMobile"></other-legal-docs>
                        </div>
                    </v-layout>
                </v-flex>
            </v-layout>
        </v-content>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha" @recaptcha-loaded="recaptchaLoaded"></recaptcha>
        
        <v-snackbar v-model="isNoInternet" color="warning" :bottom="y === 'top'" :left="x === 'left'" :multi-line="mode === 'multi-line'"
            :right="x === 'right'" :timeout="0" :top="y === 'top'" :vertical="mode === 'vertical'"
            >You have no internet connection.
        </v-snackbar>
    </v-app>
</template>
<script>

import '@/assets/css/public.css'
import '@/assets/css/season.css'
import auth from "@/auth/auth.js"
import publicPages from '@/helpers/public-pages.js'
import { getUAData } from '@/helpers/user-agent.js'

export default {
    name: 'Login',
    metaInfo: {
        title: 'Login',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Login' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'recaptcha': () => import('@/components/ReCaptcha.vue'),
        'terms-privacy': () => import('@/components/Common/TermsAndPrivacy'),       
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'public-notifications': () => import('@/components/Common/PublicNotifications'),
    },
    data () {
        return {
            loading: true,
            disabled: false,
            error: false,
            errorMessage: null,
            show1: false,
            slowInternet: false,
            seasonDetails: publicPages.getSeasonDetails(),
            rules: {
                required: value => !!value || "Required.",
                min: v => v.length >= 12 || "Min 12 characters",
                counter: value => value.length <= 50 || "Max 50 characters",
                max: value => value.length <= 50 || "Max 50 characters",
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return pattern.test(value) || "Invalid e-mail.";
                }
            },
            email: "",
            password: "",
            isNoInternet: false,
            isRecaptchaLoaded: false,
            mode: null,
            x: null,
            y: null,
            recaptchaLoadingTimer: null
         }
    },
    methods: {
        reloadPage() {
            window.location.reload()
        },
        submitReCaptcha(response) {
            this.recaptchResponse = response;   
            this.login()
        },
        executeRecaptcha() {
            if (this.$refs.form.validate()) {
                this.loading = true;

                // execute the recaptcha widget
                this.$refs.recaptcha.execute()
                this.loading = false;
            }
        },
        async login() {            
            try {
                this.error = false;
                const userAgent = await getUAData()

                if (this.$refs.form.validate()) {
                    this.loading = true
                    this.disabled = true

                    var data = {
                        email: this.email,
                        password: this.password,
                        recaptchaResponse: this.recaptchResponse,
                        
                        browserName: userAgent.browser.name,
                        deviceModel: userAgent.device.model,
                        deviceType: userAgent.device.type,
                        deviceVendor: userAgent.device.vendor,
                        oSName: userAgent.os.name,
                        oSVersion: userAgent.os.version,
                        deviceFingerprint: userAgent.fingerprint,
                        
                        sourceType: 'Customer'
                    }

                    await auth.login(data, false)
                    
                    if (auth.requiresChangePassword()) {
                        auth.deleteToken()
                        this.$router.push("/ChangePassword")
                        return;
                    }

                    if (!auth.loggedIn()) {
                        this.error = true;
                        this.errorMessage = 'Login error. Please check your email/password.'
                        this.loading = false
                        this.disabled = false
                    } else {
                        //2FA is enabled
                        if (this.$store.state.twoFA) {
                            if (!this.$store.state.twoFAVerified) {
                                auth.deleteToken()      //clear token while not otp verfied to prevent accessing authorized page
                                this.$router.push("/otp")
                            }else{
                                if (auth.onBoarded()) {
                                    this.$router.push("/")
                                }
                                else this.$router.push("/features")
                            }
                                
                        } else {
                            //if 2FA is turned off, then check user's device used
                            if (this.$store.state.newDevice) {
                                auth.deleteToken()      //clear token while not otp verfied to prevent accessing authorized page
                                this.$router.push("/otp")
                            } else {
                                auth.clearLoginDetails()
                                if (auth.onBoarded()) {
                                    this.$router.push("/")
                                } 
                                else this.$router.push("/features")
                            }
                        }
                    }
                }
            }catch(error) {
                this.errorMessage = this.parseError(error)
                this.error = true
                this.loading = false
                this.disabled = false
            }
        },
        parseError(error) {
            if (error.toString().toLowerCase().includes('500')) {
                return 'We ran into some technical problems. Please contact support@sweldonow.ph'
            }else if (error.toString().toLowerCase().includes('network') || error.toString().includes('ERR_CONNECTION_REFUSED')) {
                return 'Unable to connect to server. Please check your internet connection. If issues persist, please contact support@sweldonow.ph'
            }else{
                return 'Invalid credentials. Please check your email/password.'
            }
        },
        updateConnectionStatus() {
            this.isNoInternet = !navigator.onLine;
        },
        recaptchaLoaded() {
            this.loading = false
            this.isRecaptchaLoaded = true
        },
        checkRecaptcha() {
            if (!this.isRecaptchaLoaded) {
                this.slowInternet = true
            }else{
                this.slowInternet = false
                clearInterval(this.recaptchaLoadingTimer)
            }
        }
    },
    async created() {
        window.addEventListener('online', this.updateConnectionStatus);
        window.addEventListener('offline', this.updateConnectionStatus);
        this.updateConnectionStatus();
        this.recaptchaLoadingTimer = setInterval(this.checkRecaptcha, parseInt(5000))  //if recaptcha is not loaded in 5secs, show slow internet
    },
    computed: {
        isMobile() {
            return window.innerWidth < 600
        },
        welcomeSectionClass() {
            return this.isMobile ? this.seasonDetails.classNameMobile : this.seasonDetails.className
        },
        welcomeButtonColor() {
            return this.seasonDetails.buttonColor
        }
    }
}
</script>